import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { parseLocalDate } from "@ui/shared";

interface Props {
  /**
   * Server-side transaction status. `succeeded` shows the "all set" state
   * (typically credit cards). Anything that hasn't finalized yet (most
   * commonly `processing` for ACH) shows the "payment is being processed"
   * state. `failed`/`cancelled` aren't expected to reach this screen.
   */
  status: "succeeded" | "processing" | "pending" | "failed" | "cancelled";
  /** Policy name / Letter of Credit number, when available. */
  policyName?: string | null;
  /** Renewal date string (ISO or display-formatted). */
  renewalDate?: string | null;
}

// `renewalDate` can be either a DATETIME ISO (from PayRenewalResult) or
// a DATE-only YYYY-MM-DD (from invite.leaseEndDate fallback in
// SDAPayment). parseLocalDate handles both correctly.
const formatRenewalDate = (s: string | null | undefined): string => {
  if (!s) return "—";
  const d = parseLocalDate(s);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

const SDASuccess: React.FC<Props> = ({ status, policyName, renewalDate }) => {
  const { t } = useTranslation();

  const isCompleted = status === "succeeded";

  return (
    <div className="rounded-xl bg-white shadow-sm">
      <div className="p-8 text-center">
        <img
          alt=""
          src="/assets/thanks.png"
          className="mx-auto mb-6"
          style={{ maxWidth: "320px", width: "100%" }}
        />

        {isCompleted ? (
          <>
            <h1 className="mb-4 text-2xl font-bold text-primary">
              {t("sda.success.completedTitle")}
            </h1>

            <p className="mx-auto mb-6 max-w-xl text-sm text-gray-700">
              {t("sda.success.completedBodyPrefix")}{" "}
              <strong>
                {policyName || t("sda.success.completedBodyPolicyPlaceholder")}
              </strong>
              .
              <br />
              {t("sda.success.completedBodySuffix")}
            </p>

            <h2 className="text-base font-semibold text-primary">
              {t("sda.success.nextRenewalHeading")}
            </h2>
            <h3 className="mb-6 text-lg font-medium text-gray-800">
              {formatRenewalDate(renewalDate)}
            </h3>

            <p className="mx-auto mb-6 max-w-xl text-sm text-gray-600">
              {t("sda.success.completedFooter")}
            </p>

            <a
              target="_blank"
              rel="noreferrer"
              href="https://g.page/r/CaKzS97DZWf-EB0/review"
              className="inline-block rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light"
            >
              {t("sda.success.tellUs")}
            </a>

            <div className="mt-3">
              <Link
                to="/dashboard"
                className="text-sm font-medium text-info underline hover:text-sky-600"
              >
                {t("sda.success.seeLetterOfCredit")}
              </Link>
            </div>
          </>
        ) : (
          <>
            <h1 className="mb-4 text-2xl font-bold text-primary">
              {t("sda.success.processingTitle")}
            </h1>
            <h2 className="mb-4 text-base font-semibold text-gray-700">
              {t("sda.success.processingSubtitle")}
            </h2>
            <p className="mx-auto mb-6 max-w-xl text-sm font-medium text-gray-700">
              {t("sda.success.processingBody")}
            </p>
            <p className="mx-auto mb-6 max-w-xl text-sm text-gray-600">
              {t("sda.success.processingSupportPrefix")}
              <br />
              {t("sda.success.processingSupportPhone")}
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default SDASuccess;
